import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { downloadFile } from '../../../Company/UploadJourney/components/UploadCard/utils/api/helpers';
import { getRawMetadata } from '../../../../store/dashboard/auditedFinancials/service';
import { AUDITED_NOT_READY_TOAST, LEGACY_AUDITED_FILENAME } from '../../../../store/dashboard/auditedFinancials/constants';
import type { AuditedFinancialDoc } from '../../../../store/dashboard/auditedFinancials/types';

export { AUDITED_NOT_READY_TOAST } from '../../../../store/dashboard/auditedFinancials/constants';

interface UseAuditedReportDownloadProps {
    assesseeOrgId: string;
    orgName?: string;
}

interface UseAuditedReportDownloadResult {
    available: Pick<AuditedFinancialDoc, 'docId' | 'docName'> | null;
    downloading: boolean;
    onDownloadClick: () => Promise<void>;
}

export function useAuditedReportDownload({ assesseeOrgId, orgName = '' }: UseAuditedReportDownloadProps): UseAuditedReportDownloadResult {
    const [available, setAvailable] = useState<Pick<AuditedFinancialDoc, 'docId' | 'docName'> | null>(null);
    // resolving: true only during the mount metadata-fetch; does NOT block the button
    const [resolving, setResolving] = useState(false);
    // downloading: true only during an active file-download triggered by the user
    const [downloading, setDownloading] = useState(false);

    useEffect(() => {
        if (!assesseeOrgId) return;

        let cancelled = false;
        setResolving(true);

        getRawMetadata(assesseeOrgId)
            .then((metadataRes) => {
                if (cancelled) return;

                const auditedFinancials: AuditedFinancialDoc[] = metadataRes?.data?.data?.auditedFinancials ?? [];

                const match = auditedFinancials.find((doc) => doc.docId && !doc.isDeleted && doc.docName !== LEGACY_AUDITED_FILENAME);
                if (match) {
                    setAvailable({ docId: match.docId, docName: match.docName });
                } else {
                    setAvailable(null);
                }
            })
            .catch(() => {
                if (!cancelled) setAvailable(null);
            })
            .finally(() => {
                if (!cancelled) setResolving(false);
            });

        return () => {
            cancelled = true;
        };
    }, [assesseeOrgId]);

    const onDownloadClick = async () => {
        // If resolution is still in-flight, treat as unavailable — toast, do not block silently
        if (resolving || !available) {
            toast.error(AUDITED_NOT_READY_TOAST);
            return;
        }
        setDownloading(true);
        try {
            const sanitizedOrgName = orgName.replace(/[\\/:*?"<>|]/g, '');
            const lastDot = available.docName.lastIndexOf('.');
            const base = lastDot !== -1 ? available.docName.slice(0, lastDot) : available.docName;
            const ext = lastDot !== -1 ? available.docName.slice(lastDot) : '';
            const saveAsName = sanitizedOrgName ? `${base} - ${sanitizedOrgName}${ext}` : available.docName;
            await downloadFile(available.docId, undefined, saveAsName);
        } catch {
            toast.error(AUDITED_NOT_READY_TOAST);
        } finally {
            setDownloading(false);
        }
    };

    return { available, downloading, onDownloadClick };
}
